import {getGlobal, isNodeJS, isMobileDevice} from '../compat';
import {makeDtViewer3DImplProxy} from './DtViewer3DImpl';
import ViewObjectIn3D from '../lmv-compat/tools/ViewObjectIn3D';
import {fitToView, get3DSelectionBounds} from "../lmv-compat/tools/FitToViewUtil";
import {flyToView, transitionToView} from "../lmv-compat/tools/ViewTransition";
import {SETTINGS_PANEL_CREATED_EVENT} from "../gui/GuiViewerToolbarConst";
import {DtViewerSettingsPanel} from "./gui/DtViewerSettingsPanel";
import {DtProgressBar} from "../lmv-compat/ui/DtProgressBar";
import {StatusBar} from "../gui/StatusBar";

import { DtPrefs, DtPrefs3D } from './preferences/DtPreferenceNames';
import { TandemProfile, DEFAULT_STORAGE_PREFIX } from './preferences/DtProfile';
import { DtLoader } from './loader/DtLoader';

import { av, avp, avu } from "@adsk/lmv";

/**
 * A factory that applies our viewer API customizations to the given LMV viewer class.
 * @param {class} ViewerClass
 * @returns {class} Tandem viewer class
 */
export function InheritFromViewerClass(ViewerClass) {
	return class extends ViewerClass {

		#loadingFpsTargetActive;
		#origFpsTarget;
		#loadingFpsTarget = 15;

		constructor(container, config) {
			config = config || {};

			if (!config.fileLoader) {
				config.fileLoader = DtLoader;
			}

			if (!config.localStoragePrefix) {
				config.localStoragePrefix = DEFAULT_STORAGE_PREFIX;
			}

			// This replaces the 'powered by Autodesk' loading spinner with more neutral dots.
			Autodesk.Viewing.Private.DISABLE_FORGE_LOGO = true;

			super(container, config);

			this.config = config;

			if (container) {
				this.canvas.style.display = 'block'; // prevent default inline behavior from adding invisible height.
			}

			this._sessionId = config?.sessionId;
			this._featureFlags = undefined;

			this.addEventListener(av.ESCAPE_EVENT, function() {
				// Deselect
				if (this.impl.selector.hasSelection()) {
					this.clearSelection();
					return;
				}

				// Reset default navigation mode:
				if (!this.skipEscNavHandling && this.getActiveNavigationTool() !== this.getDefaultNavigationToolName()) {
					// Force unlock active tool:
					if (this.toolController)
						this.toolController.setIsLocked(false);

					this.setActiveNavigationTool();
					return;
				}

				// Show all if anything is hidden
				if (!this.areAllVisible()) {
					this.showAll();
					return;
				}

				this.extraEscapeEventHandler();
			});

			this.impl = makeDtViewer3DImplProxy(this.impl);
		}

		// ----- LMV class overrides -----

		start(url, options, onSuccessCallback, onErrorCallback, initOptions) {
			super.start(url, options, onSuccessCallback, onErrorCallback, initOptions);

			// Load extension that are used by the SDK.
			// A mapping of extension name -> options
			const extensions = {
				'Autodesk.Tandem.DtCompGeom': {},
				'Autodesk.Tandem.ViewCubeUi': {}
			};

			if (!isMobileDevice()) {
				extensions['Autodesk.BoxSelection'] = {
					// Note that we cannot use the constants defined by the extension, because it's not loaded yet.
					options: { type: 'geometric', selectionType: av.SelectionType.MIXED },
					loadCb: function(extension) {
						extension.setMode?.('enclosed', 'ltr');
						extension.setMode?.('intersected', 'rtl');
						// Register a filter function that rejects elements from 2D models.
						// Selection for 2D models is currently not supported.
						extension.setFilter((hit) => {
							if (hit.model.is2d()) {
								return false;
							}
							return true;
						});

						// LMV's BoxSelection extension has a hard-coded value to not add a button to the toolbar
						// (i.e. extension.addToolbarButton(false)). We override the method to change that value to
						// true, since we want its button to show on the toolbar when the extension is loaded.
						const origAddToolbarButton = extension.addToolbarButton;
						extension.addToolbarButton = function() {
							origAddToolbarButton.call(extension, true);
						}
					}
				};
			}

			for (const extensionId in extensions) {
				const loadPromise = this.loadExtension(extensionId, extensions[extensionId].options);
				if (extensions[extensionId].loadCb) {
					loadPromise.then(extensions[extensionId].loadCb).catch(function(error) {
						console.error(`Failed loading extension ${extensionId}: ${error}`);
					})
				}
			}
		}

		tearDown() {
			if (this.viewerSettingsPanel) {
				this.setSettingsPanel(null);
			}

			if (this.statusbar) {
				this.prefs.removeListeners(DtPrefs.DISPLAY_OBJECT_INFO, this.onStatusBarPrefChanged, null);
				this.statusbar.dtor();
				this.statusbar = null;
			}

			super.tearDown();
		}

		initialize() {
			const result = super.initialize();

			if (result !== 0) {
				return result;
			}

			this.setVisibleViewportArea();

			// Create a progress bar.
			if (this.progressbar) {
				// If this is the GuiViewer, the base class may have already created a progress bar.
				// It's of the wrong type though (ProgressBar instead of our DtProgressBar`), so we replace it
				// with a new instance below. However, the existing progress bar created UI elements that cannot be
				// removed (no dispose method), leading to an 'empty' (=0%) progress bar zombie.
				// We set its progress to 100% as a work-around, to make it invisible.
				this.progressbar.setPercent(100);
			}
			this.progressbar = new DtProgressBar(this.container, true);
			this.addEventListener(av.PROGRESS_UPDATE_EVENT, this.onProgressBarUpdate);

			this.statusbar = new StatusBar(this.container);
			this.onStatusBarPrefChanged = (value) => {
				this.statusbar.toggleVisibility(value);
			}

			// We have to run this here (would otherwise do it in setUp), because there are cases where loading a
			// facility tries to access some settings that aren't configured yet, since LMV runs setUp in a timeout.
			this.initPreferencesProfile();

			return result;
		}

		uninitialize() {
			super.uninitialize();

			this.removeEventListener(av.PROGRESS_UPDATE_EVENT, this.onProgressBarUpdate);
			this.progressbar = null;

			if (this.viewerSettingsPanel) {
				this.viewerSettingsPanel.uninitialize();
				this.viewerSettingsPanel = null;
			}
		}

		createControls() {
			const toolController = super.createControls();

			toolController.registerTool(new av.FovTool(this));

			// Add some custom methods to the viewing utilities.
			// TODO: Would be nicer to use a custom instance of ViewingUtilities,
			// but I don't see how to do that in a clean way.

			this.utilities.navapi = this.navigation;

			this.utilities.defaultHomeAction = function() {
				this.autocam.goHome();
			};

			this.utilities.homeAction = this.utilities.defaultHomeAction;

			this.utilities.viewObjectIn3D = new ViewObjectIn3D(this);

			/**
			 * Provide an alternate home action (e.g., for Home button and 'H' shortcut key).
			 * To restore the default home action, supply null or undefined action
			 */
			this.utilities.setHomeAction = function (action) {
				this.homeAction = action ?? this.defaultHomeAction;
			};

			this.utilities.goHome = function() {
				this.viewerImpl.track({ name: 'navigation/home', aggregate: 'count' });
				this.homeAction();
			};

			/**
			 * Request a camera transition to an occlusion free view of the object at first person height
			 *
			 * @param {DtModel} model object model
			 * @param {Number} dbId object dbId
			 * @param {Number} duration duration of the camera transition in seconds
			 * @returns {Promise|null} resolves after camera transition. Falls back to fitToView and returns null if a good occlusion free first person view not found.
			 */
			this.utilities.flyToObject = async function (model, dbId, duration = 1) {
				const viewForObject = this.viewObjectIn3D.getViewForObject(model, dbId);
				if (viewForObject) {
					return new Promise((resolve) => flyToView(this.viewerImpl.api, viewForObject, duration, resolve));
				}

				console.warn(
					"Couldn't find a good first person view for the object. Falling back to fitting the object to the view."
				);
				fitToView(this.viewerImpl.api, [{ model, selection: [dbId] }]);
				return null;
			};

			this.utilities.transitionToView = function (finalSelection, transientSelection, duration = 1) {
				const viewer = this.viewerImpl.api;
				const currentBounds = get3DSelectionBounds(viewer, viewer.getAggregateSelection());
				const finalBounds = get3DSelectionBounds(viewer, finalSelection);

				const isSingleSelection = finalSelection.length === 1 && finalSelection[0].selection.length === 1;
				const camera = viewer.getCamera();
				const projMatrix = new THREE.Matrix4().multiplyMatrices(camera.projectionMatrix, camera.matrixWorldInverse);
				const viewFrustum = new THREE.Frustum().setFromProjectionMatrix(projMatrix);

				if (!transientSelection?.length || viewFrustum.containsPoint(finalBounds.getCenter())) {
					// Already in view
					viewer.setAggregateSelection(finalSelection);
					camera.isPerspective && isSingleSelection
						? this.flyToObject(finalSelection[0].model, finalSelection[0].selection[0], duration)
						: this.fitToView();
					return;
				}

				// Final bounds are not "mostly" in view, fly towards it passing through the transient selection
				const pos = this.navapi.getPosition();
				const oldcoi = this.navapi.getTarget();
				const fov = this.navapi.getVerticalFov();

				const facilityBounds = this.viewerImpl.getVisibleBounds(false, false);
				const currentTarget = currentBounds.getCenter();
				const transientTarget = get3DSelectionBounds(viewer, transientSelection).getCenter();

				// Project selections' bounds' centers upwards to top of facility box
				// TODO: could be room/level bounds when isolated so we don't always zoom to the stratosphere
				const control1 = { position: currentTarget.clone().setZ(facilityBounds.max.z), target: currentTarget, up: camera.up };
				const control2 = { position: transientTarget.clone().setZ(facilityBounds.max.z), target: transientTarget, up: camera.up };
				const finalFit = this.navapi.computeFit(control2.position, control2.target, fov, finalBounds, camera.aspect);

				const onFinished = (camera.isPerspective && isSingleSelection)
					? () => viewer.utilities.flyToObject(finalSelection[0].model, finalSelection[0].selection[0], 1)
					: () => {
						const finalFit = this.navapi.computeFit(pos, oldcoi, fov, finalBounds, camera.aspect);
						this.navapi.setRequestTransitionWithUp(true, finalFit.position, finalFit.target, fov, this.navapi.computeOrthogonalUp(pos, oldcoi));
						this.navapi.setPivotPoint(finalFit.target);
						this.navapi.setPivotSetFlag(true);
					};

				transitionToView(viewer, control1, control2, finalFit, duration, onFinished);
			}

			return toolController;
		}

		initContextMenu() {
			if (!this.contextMenu) {
				this.setDefaultContextMenu();
			}

			super.initContextMenu();
		}

		initializePrefListeners() {
			super.initializePrefListeners();

			this.prefs.addListeners(DtPrefs.ENABLE_MODEL_CACHE, (value) => {
				getGlobal().ENABLE_MODEL_CACHE = value;
			});

			this.prefs.addListeners(DtPrefs.DISPLAY_OBJECT_INFO, this.onStatusBarPrefChanged, null);
		}

		// TODO: Remove this after making sure that dt-client always provides dbIds as numbers.
		setAggregateSelection(selection, fireEvent) {
			// Tandem provides dbIds as strings in some places. We try to parse them as numbers here,
			// because this is what LMV expects.
			if (selection) {
				for (const perModel of selection) {
					const ids = perModel.ids ?? perModel.selection;
					for (let i = 0; i < ids.length; ++i) {
						const id = ids[i];
						if (typeof id === 'string') {
							ids[i] = Number.parseInt(id);
						}
					}
				}
			}

			// LMV's setAggregateSelection only updates the models present in `selection`, leaving other model's existing 
			// selection untouched when not explicitly included as an empty selection. Callers expect this to fully 
			// replace the aggregate selection, so clear any currently-selected model left out of `selection`.
			//
			// TODO: LMV will include this behavior when V8 is released, remove this logic once we're on that version.
			let fullSelection = selection;
			if (selection?.length) {
				const includedModels = new Set(selection.map((perModel) => perModel.model));
				const missing = this.getAggregateSelection().filter((current) => !includedModels.has(current.model));
				if (missing.length) {
					fullSelection = selection.concat(missing.map((current) => ({ model: current.model, selection: [] })));
				}
			}

			super.setAggregateSelection(fullSelection, fireEvent);
		}

		// ----- LMV class overrides end -----
		// ----- Tandem added -----

		setFeatureFlags(flags) {
			this._featureFlags = flags;
		}

		getFeatureFlags() {
			return this._featureFlags;
		}

		initPreferencesProfile(options = {}) {
			// Setup of AO, Ghosting, Env Lighting etc.
			// This will also setup the viewer.profile only for the web version of the viewer.
			// This should work with the nodejs version, but loading the settings from the loadModel function causes some
			// methods to be undefined.

			// Initialize the profile only if the viewer.profile is not set.
			// This will insure that the loadModel model functions sets the settings once.
			if (!options.skipPrefs) {
				if (!isNodeJS() && !this.profile) {
					let profile = options.profile || TandemProfile;
					this.setProfile(profile, false);
				}
			} else {
				// These preferences are expected to be always available even if no profile is loaded
				if (this.prefs.get(DtPrefs.DISPLAY_UNITS) === undefined) {
					this.prefs.set(DtPrefs.DISPLAY_UNITS, new av.EnumType(displayUnitsEnum));
				}
				if (this.prefs.get(DtPrefs.DISPLAY_UNITS_PRECISION) === undefined) {
					this.prefs.set(DtPrefs.DISPLAY_UNITS_PRECISION, new av.EnumType(displayUnitsPrecisionEnum));
				}
			}
		}

		loadModelDt(modelRootUrl, options = {}) {
			if (!options.keepCurrentModels && this.impl.hasModels()) {
				let _conf = this.config;
				this.tearDown();
				this.setUp(_conf);
			}

			return new Promise((resolve, reject) => {
				// The error handler receives multiple parameters, so we construct a more meaningful error from them.
				const errorHandler = (code, message, args) => {
					const error = { message, details: args };
					reject(error);
				}
				this.loadModel(modelRootUrl, options, resolve, errorHandler);
			});
		}

		async loadPdfFromUrn(urn, options) {
			if (!this.getExtension('Autodesk.PDF')) {
				await this.loadExtension('Autodesk.PDF');
			}

			// Create a fake bubble / document that we can feed into loadDocumentNode.
			// TODO: urn is the URN of a vector PDF on derivative service.
			// Should this be the urn of a manifest, so that we can extract the correct stats (e.g. size) from it?
			const bubble = this.#buildPdfBubble(urn);
			const doc = new av.Document(bubble, 'placeholder.pdf');
			const pageNode = doc.getRoot().children[0];

			const model = await this.loadDocumentNode(doc, pageNode, options);
			return model;
		}

		#buildPdfBubble(urn) {
			return {
				guid: 'fake-dt-pdf-bubble',
				type: 'design',
				role: 'viewable',
				status: 'success',
				isVectorPDF: true,
				children: [
					{
						guid: 'fake-dt-pdf-page-1',
						type: 'geometry',
						role: '2d',
						status: 'success',
						page: 1,
						children: [
							{
								role: 'pdf-page',
								mime: "application/pdf",
								type: 'resource',
								status: 'success',
								page: 1,
								urn
							}
						],
					},
				],
			};
		}

		getSelectionMode() {
			return this.prefs.get(DtPrefs3D.SELECTION_MODE);
		}

		createSettingsPanel() {
			var settingsPanel = new DtViewerSettingsPanel(this);
			this.setSettingsPanel(settingsPanel);
			settingsPanel.syncUI();

			this.toolbar?.initSettingsOptionsButton();

			this.dispatchEvent({ type: SETTINGS_PANEL_CREATED_EVENT });
		}

		getSettingsPanel() {
			if (!this.viewerSettingsPanel) {
				this.createSettingsPanel();
			}
			return this.viewerSettingsPanel;
		}

		setSettingsPanel(settingsPanel) {
			if (settingsPanel instanceof avu.SettingsPanel || !settingsPanel) {
				if (this.viewerSettingsPanel) {
					this.viewerSettingsPanel.setVisible(false);
					this.viewerSettingsPanel.uninitialize();
					this.viewerSettingsPanel = null;
				}

				this.viewerSettingsPanel = settingsPanel;
			}
		}

		showViewer3dOptions(show) {
			var settingsPanel = this.getSettingsPanel(true);
			if (show && settingsPanel.isVisible()) {
				settingsPanel.setVisible(false);
			}
			settingsPanel.setVisible(show);
		}

		/**
		 * An object that holds position details of the visible portion of the viewer that mimics DOMRect structure
		 * @typedef {Object} VisibleViewportArea
		 * @property {number} x - The x-coordinate of the rectangle's origin.
		 * @property {number} y - The y-coordinate of the rectangle's origin.
		 * @property {number} width - The width of the rectangle.
		 * @property {number} height - The height of the rectangle.
		 * @property {number} top - The y-coordinate of the top edge of the rectangle.
		 * @property {number} right - The x-coordinate of the right edge of the rectangle.
		 * @property {number} bottom - The y-coordinate of the bottom edge of the rectangle.
		 * @property {number} left - The x-coordinate of the left edge of the rectangle.
		 */

		/**
		 * Sets the amount of viewable space the viewer has
		 * If details is not provided then resets to the default viewable space.
		 * @param {VisibleViewportArea} details
		 */
		setVisibleViewportArea(details) {
			if (!details) {
				const rect = this.container.getBoundingClientRect();
				this.visibleViewportArea = {
					x: rect.x,
					y: rect.y,
					width: rect.width,
					height: rect.height,
					top: rect.top,
					right: rect.right,
					bottom: rect.bottom,
					left: rect.left
				};
				return;
			}
			this.visibleViewportArea = details;
		}

		/**
		 * Returns the details of the viewable area the viewer has, in the form of DOMRect object
		 * @returns {VisibleViewportArea}
		 */
		getVisibleViewportArea() {
			return this.visibleViewportArea;
		}

		onProgressBarUpdate(event) {
			if (event.percent >= 0) {
				this.progressbar.setPercent(event.percent, event.state);
			}
		}

		getStatusBar() {
			return this.statusbar;
		}

		deselectNodes(model, dbIds, fireAggregateSelectionChangedEvent = false) {
			const selector = model.selector;
			let changed = false;
			for (let dbId of dbIds) {
				if (selector.isSelected(dbId)) {
					selector._deselect(dbId);
					changed = true;
				}
			}

			if (changed && fireAggregateSelectionChangedEvent) {
				this.selector._fireAggregateSelectionChangedEvent();
			}
		}

		extraEscapeEventHandler() {
			// no-op. Overridden in DtGuiViewer3D.
		}

		/**
		 * Sets a temporary, low FPS target to render more geometry during the load process.
		 * @param {Boolean} value true to enable the load process target, false to reset to the user preference or default value.
		 */
		setLoadingFpsTarget(value) {
			if (value && this.#loadingFpsTargetActive) {
				return;
			}

			this.#loadingFpsTargetActive = value;

			if (value) {
				// Set to 15 FPS while loading.
				// We avoid calling this.setProgressiveFpsTarget(target), because it updates the user preferences.
				// If loading is cancelled, the user is stuck with this temporary target.
				// So we set it on a lower level instead.
				this.impl.setFPSTargets(1, 15, 15 / 2);
			} else {
				// Reset to the user preference, or default value.
				const targetFromSettings = this.prefs.get(DtPrefs.PROGRESSIVE_FPS_TARGET);
				const target = targetFromSettings ?? avp.FPS_TARGET_MODES.MEDIUM
				this.impl.setFPSTargets(1, target, target / 2);
			}
		}

		// ----- Tandem added end -----
	}
}

export const DtViewer3D = InheritFromViewerClass(av.Viewer3D);
